import { API_PROXY, IS_PRODUCTION } from "@/constants/env";
import { useVendorContext } from "@/contexts/vendor-context";
import useSesamyAuthFetch from "@/hooks/use-sesamy-auth-fetch";
import useUniversalRouter from "@/hooks/use-universal-router";
import { useAuth0, type User as Auth0User } from "@auth0/auth0-react";
import { datadogRum } from "@datadog/browser-rum";
import Intercom, { update } from "@intercom/messenger-js-sdk";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";

const ERROR_IGNORE_LIST = ["ResizeObserver loop limit exceeded"];

const getPortalUrl = () =>
  typeof window !== "undefined"
    ? window.location.origin
    : "https://portal.sesamy.com";

const initializeDatadog = (user: Auth0User) => {
  if (!IS_PRODUCTION) {
    return;
  }

  datadogRum.init({
    applicationId: "c7fcdbcc-b66b-4f9c-b475-8a04f5c02ea8",
    clientToken: "pub2dfaadbfe92b58f818a76e0c6bad6dd4",
    site: "datadoghq.eu",
    service: "insights",
    env: "production",
    version: "1.0.0",
    sampleRate: 100,
    sessionReplaySampleRate: 100,
    trackInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    beforeSend: (
      _,
      // this has type but they're wrong.  have to cast to any... typescript says error key doesn't exist but does from logs
      context: any,
    ) => {
      if (context.error && ERROR_IGNORE_LIST.includes(context.error)) {
        return false;
      }
    },
  });

  datadogRum.startSessionReplayRecording();

  datadogRum.setUser({
    sub: user.sub,
    email: user.email || undefined, // TypeScript dance for RUM. Can't get RUM's User type from package
    name: user.name || undefined,
  });
};

const UserContext = () => {
  const { user } = useAuth0();
  const { pathname } = useUniversalRouter();
  const { selectedVendor, getVendorSettings, vendors, multiVendorKey } =
    useVendorContext();
  const { get: authGet, isReady: isAuthReady } = useSesamyAuthFetch();
  const vendorSettings = getVendorSettings(selectedVendor);
  const [hasInitializedDatadog, setHasInitializedDatadog] = useState(false);
  const [hasInitializedIntercom, setHasInitializedIntercom] = useState(false);
  const [isPosthogIdentified, setIsPosthogIdentified] = useState(false);
  const [posthogGroup, setPosthogGroup] = useState<string | null>(null);
  const intercomSettingsRef = useRef<Parameters<typeof Intercom>[0] | null>(
    null,
  );

  useEffect(() => {
    if (!user) return;

    const { name, email, sub, picture } = user;

    try {
      if (!hasInitializedDatadog) {
        setHasInitializedDatadog(true);
        initializeDatadog(user);
      }
      if (
        !hasInitializedIntercom &&
        typeof window !== "undefined" &&
        window.location.hostname !== "localhost"
      ) {
        setHasInitializedIntercom(true);
        intercomSettingsRef.current = {
          app_id: "rhhdaakf",
          user_id: sub,
          name,
          email,
          avatar: picture
            ? {
                type: "avatar",
                image_url: picture,
              }
            : undefined,
          company:
            vendorSettings && selectedVendor !== multiVendorKey
              ? {
                  id: vendorSettings.vendorId,
                  name: vendorSettings.name,
                  website: vendorSettings.siteUrl,
                  country: vendorSettings.country,
                  currency: vendorSettings.currency,
                  plan: "portal-default",
                  portal_url: `${getPortalUrl()}/${vendorSettings.vendorId}`,
                }
              : undefined,
          source: "portal",
          vendor_count:
            vendors?.filter((v) => v !== multiVendorKey).length || 0,
          hide_default_launcher: false,
          alignment: "left",
          horizontal_padding: 10,
          vertical_padding: 10,
          action_color: "#6B53F3",
          background_color: "#6B53F3",
        } as Parameters<typeof Intercom>[0];
        Intercom(intercomSettingsRef.current);
      }
      if (vendorSettings && !isPosthogIdentified) {
        setIsPosthogIdentified(true);
        posthog.identify(sub, {
          email,
          name,
        });
        if (email?.endsWith("@sesamy.com")) {
          posthog.group("company", "sesamy", {
            name: "Sesamy",
          });
        }
      }
      if (
        vendorSettings &&
        (!posthogGroup || posthogGroup !== vendorSettings.vendorId)
      ) {
        setPosthogGroup(vendorSettings.vendorId);
        posthog.group("vendor", vendorSettings.vendorId, {
          name: vendorSettings.name,
        });
      }
    } catch (e) {
      console.error(e);
    }
  }, [
    user,
    hasInitializedDatadog,
    hasInitializedIntercom,
    isPosthogIdentified,
    posthogGroup,
    vendorSettings,
  ]);

  useEffect(() => {
    if (!intercomSettingsRef.current) return;

    const isRealVendor = vendorSettings && selectedVendor !== multiVendorKey;

    intercomSettingsRef.current = {
      ...intercomSettingsRef.current,
      company: isRealVendor
        ? {
            id: vendorSettings.vendorId,
            name: vendorSettings.name,
            website: vendorSettings.siteUrl,
            country: vendorSettings.country,
            currency: vendorSettings.currency,
            plan: "portal-default",
            portal_url: `${getPortalUrl()}/${vendorSettings.vendorId}`,
          }
        : undefined,
      vendor_id: isRealVendor ? vendorSettings.vendorId : undefined,
    } as Parameters<typeof Intercom>[0];
    update(intercomSettingsRef.current);
  }, [vendorSettings, selectedVendor, multiVendorKey]);

  useEffect(() => {
    if (!isAuthReady || !intercomSettingsRef.current) return;

    const fetchIntercomJwt = async () => {
      try {
        const { token } = await authGet({
          url: `${API_PROXY}/support-session`,
        });

        intercomSettingsRef.current = {
          ...intercomSettingsRef.current!,
          intercom_user_jwt: token,
        } as Parameters<typeof Intercom>[0];
        update(intercomSettingsRef.current);
      } catch (e) {
        console.error("Failed to fetch Intercom JWT:", e);
      }
    };

    fetchIntercomJwt();
  }, [isAuthReady]);

  useEffect(() => {
    if (intercomSettingsRef.current) {
      update(intercomSettingsRef.current);
    }
  }, [pathname]);

  return null;
};

export default UserContext;
